import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Space,
  Button,
  Row,
  Col,
  Statistic,
  Tag,
  Select,
  message,
  Descriptions,
  Badge,
  Drawer
} from 'antd';
import DataTable from '@/components/DataTable';
import {
  BlockOutlined,
  SafetyOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  HistoryOutlined,
  BranchesOutlined
} from '@ant-design/icons';
import WorkflowTimelineAntd from '@/components/WorkflowTimeline/WorkflowTimelineAntd';
import ESignatureModalAntd from '@/components/ESignatureModal/ESignatureModalAntd';
import {
  STANDARD_BLOCK_VERSIONING_WORKFLOW,
  STANDARD_BLOCK_WORKFLOW_SUMMARY,
  APPROVAL_MATRIX
} from '@/constants/workflowConstants';
import MainCard from '@/components/MainCard';
import { defaultColors } from '@/themes/antd-theme';
import { useTheme } from '@/App';
import Icons from '@/components/third-party/Icons';
import { useTranslation } from 'react-i18next';
import { useMbmrAlias } from '@/hooks/useAppSetting';
import './StandardBlockVersioning.css';

const { Title, Text } = Typography;
const { Option } = Select;

/**
 * StandardBlockVersioning Page
 * Manages Standard Block Change Control and Versioning Workflow
 * 21 CFR Part 11 Compliant
 */
const StandardBlockVersioning = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const mbmrAlias = useMbmrAlias();
  const [currentStage, setCurrentStage] = useState(1);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [workflowData, setWorkflowData] = useState(null);
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [selectedStageForApproval, setSelectedStageForApproval] = useState(null);
  const [impactDrawerOpen, setImpactDrawerOpen] = useState(false);

  // Dark mode colors
  const cardBgColor = isDark ? defaultColors.palette.gray[800] : '#ffffff';
  const textColor = isDark ? '#ffffff' : '#1f2937';
  const secondaryTextColor = isDark ? defaultColors.palette.gray[400] : '#6b7280';
  const borderColor = isDark ? defaultColors.palette.gray[600] : '#e5e7eb';

  // Mock standard block data - Replace with actual API call
  const mockStandardBlocks = [
    {
      id: 1,
      name: 'Granulation Process',
      blockId: 'SB-GRAN-001',
      version: '1.3',
      status: 'Under Change Control',
      changeId: 'SBCR-2024-001',
      affectedRecipes: 12
    },
    {
      id: 2,
      name: 'Coating Process',
      blockId: 'SB-COAT-002',
      version: '2.0',
      status: 'Impact Assessment',
      changeId: 'SBCR-2024-002',
      affectedRecipes: 8
    },
    {
      id: 3,
      name: 'Packaging Line A',
      blockId: 'SB-PKG-003',
      version: '1.1',
      status: 'QA Approval',
      changeId: 'SBCR-2024-003',
      affectedRecipes: 25
    }
  ];

  // Mock affected recipes data
  const mockAffectedRecipes = [
    { key: 1, recipeName: 'ASPIRIN-100MG-TAB', recipeId: 'RCP-001', impact: 'High', reApprovalRequired: 'Yes' },
    { key: 2, recipeName: 'PARACETAMOL-500MG-CAP', recipeId: 'RCP-002', impact: 'Medium', reApprovalRequired: 'Yes' },
    { key: 3, recipeName: 'IBUPROFEN-200MG-TAB', recipeId: 'RCP-003', impact: 'Low', reApprovalRequired: 'No' },
    { key: 4, recipeName: 'METFORMIN-850MG-TAB', recipeId: 'RCP-004', impact: 'Medium', reApprovalRequired: 'Yes' },
  ];

  const impactColumns = [
    {
      title: `${mbmrAlias} Name`,
      dataIndex: 'recipeName',
      key: 'recipeName',
      width: '35%'
    },
    {
      title: `${mbmrAlias} ID`,
      dataIndex: 'recipeId',
      key: 'recipeId',
      width: '20%'
    },
    {
      title: 'Impact Level',
      dataIndex: 'impact',
      key: 'impact',
      width: '20%',
      render: (impact) => {
        const color = impact === 'High' ? 'red' : impact === 'Medium' ? 'orange' : 'green';
        return <Tag color={color}>{impact}</Tag>;
      }
    },
    {
      title: 'Re-Approval Required',
      dataIndex: 'reApprovalRequired',
      key: 'reApprovalRequired',
      width: '25%',
      render: (required) => (
        <Tag color={required === 'Yes' ? 'warning' : 'success'}>{required}</Tag>
      )
    }
  ];

  useEffect(() => {
    // Load selected block workflow data
    if (selectedBlock) {
      loadWorkflowData(selectedBlock);
    }
  }, [selectedBlock]);

  const loadWorkflowData = (blockId) => {
    // Mock API call - Replace with actual API
    const mockData = {
      blockId: blockId,
      currentStage: 2,
      stage1_block_init_data: {
        approver: 'Sarah Johnson (Process Engineer)',
        date: '2024-01-10',
        status: 'Approved'
      }
    };
    setWorkflowData(mockData);
    setCurrentStage(mockData.currentStage);
  };

  const handleBlockSelect = (value) => {
    setSelectedBlock(value);
    message.info(`Loading workflow for Standard Block ID: ${value}`);
  };

  const handleApprovalClick = (stage) => {
    setSelectedStageForApproval(stage);
    setSignatureModalOpen(true);
  };

  const handleSignatureSubmit = async (signatureData) => {
    try {
      // Mock API call to save signature and progress workflow
      console.log('Signature Data:', signatureData);

      message.success({
        content: `Stage ${selectedStageForApproval.stage} approved successfully!`,
        duration: 3
      });

      // Progress to next stage
      setCurrentStage((prev) => Math.min(prev + 1, STANDARD_BLOCK_VERSIONING_WORKFLOW.length));

      setSignatureModalOpen(false);
      setSelectedStageForApproval(null);
    } catch (error) {
      message.error('Failed to submit signature. Please try again.');
      throw error;
    }
  };

  const handleStageAction = (action, stage) => {
    if (action === 'view_impact' || stage.stageCode === 'BLOCK_IMPACT') {
      setImpactDrawerOpen(true);
    } else {
      message.info(`Action: ${action} for stage ${stage.stage}`);
    }
  };

  return (
    <MainCard
    className="m-2.5 border border-gray-300 shadow-md"
      title={
        <div className="flex items-center gap-2">
          <Icons.Box className="text-blue-600 ml-4" size={24} />
          <Title level={4} style={{ color: textColor, margin: 0, padding: 0 }}>
            {t('Standard Block Versioning & Change Control')}
          </Title>
        </div>
      }
      content={false}
    >
      <div style={{ padding: 24 }}>
        {/* Header Stats */}
        <Card style={{ marginBottom: 24, backgroundColor: isDark ? '#0F172A' : defaultColors.primary  }}>
          <Row gutter={[24, 24]}>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title={<Text style={{ color: 'rgba(255,255,255,0.85)' }}>Total Stages</Text>}
                value={STANDARD_BLOCK_WORKFLOW_SUMMARY.totalStages}
                prefix={<FileTextOutlined style={{ color: '#fff' }} />}
                valueStyle={{ color: '#fff' }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title={<Text style={{ color: 'rgba(255,255,255,0.85)' }}>E-Signatures Required</Text>}
                value={STANDARD_BLOCK_WORKFLOW_SUMMARY.mandatoryElectronicSignatures}
                prefix={<SafetyOutlined style={{ color: '#fff' }} />}
                valueStyle={{ color: '#fff' }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title={<Text style={{ color: 'rgba(255,255,255,0.85)' }}>Audit Trail Points</Text>}
                value={STANDARD_BLOCK_WORKFLOW_SUMMARY.auditTrailPoints}
                prefix={<HistoryOutlined style={{ color: '#fff' }} />}
                valueStyle={{ color: '#fff' }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Statistic
                title={<Text style={{ color: 'rgba(255,255,255,0.85)' }}>Avg Duration</Text>}
                value={STANDARD_BLOCK_WORKFLOW_SUMMARY.averageDuration}
                prefix={<ClockCircleOutlined style={{ color: '#fff' }} />}
                valueStyle={{ color: '#fff', fontSize: 16 }}
              />
            </Col>
          </Row>
        </Card>

        {/* Standard Block Selection */}
        <Card
          title={<span style={{ color: textColor }}>Select Standard Block for Change Control</span>}
          style={{ marginBottom: 24, backgroundColor: cardBgColor, borderColor }}
        >
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Select
              style={{ width: '100%' }}
              placeholder="Select a standard block to manage"
              onChange={handleBlockSelect}
              size="large"
              showSearch
              optionFilterProp="children"
            >
              {mockStandardBlocks.map((block) => (
                <Option key={block.id} value={block.id}>
                  <Space>
                    <Text strong>{block.name}</Text>
                    <Tag color="purple">v{block.version}</Tag>
                    <Tag color={block.status === 'Draft' ? 'default' : 'processing'}>
                      {block.status}
                    </Tag>
                    <Tag color="cyan" icon={<BranchesOutlined />}>
                      {block.affectedRecipes} recipes
                    </Tag>
                  </Space>
                </Option>
              ))}
            </Select>

            {selectedBlock && (
              <>
                <Descriptions
                  bordered
                  size="small"
                  column={{ xs: 1, sm: 2, md: 4 }}
                  labelStyle={{ color: secondaryTextColor, backgroundColor: isDark ? defaultColors.palette.gray[700] : undefined }}
                  contentStyle={{ color: textColor, backgroundColor: isDark ? defaultColors.palette.gray[800] : undefined }}
                >
                  <Descriptions.Item label="Block ID">
                    {mockStandardBlocks.find((b) => b.id === selectedBlock)?.blockId}
                  </Descriptions.Item>
                  <Descriptions.Item label="Current Version">
                    <Tag color="purple">
                      v{mockStandardBlocks.find((b) => b.id === selectedBlock)?.version}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Change Control ID">
                    {mockStandardBlocks.find((b) => b.id === selectedBlock)?.changeId}
                  </Descriptions.Item>
                  <Descriptions.Item label="Status">
                    <Badge
                      status="processing"
                      text={<span style={{ color: textColor }}>{mockStandardBlocks.find((b) => b.id === selectedBlock)?.status}</span>}
                    />
                  </Descriptions.Item>
                  <Descriptions.Item label={`Affected ${mbmrAlias}s`}>
                    <Tag color="cyan" icon={<BranchesOutlined />}>
                      {mockStandardBlocks.find((b) => b.id === selectedBlock)?.affectedRecipes} {mbmrAlias}s
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Current Stage">
                    <Tag color="processing">
                      Stage {currentStage} - {STANDARD_BLOCK_VERSIONING_WORKFLOW[currentStage - 1]?.stageName}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Cascade Impact">
                    <Tag color="warning">Auto-versioning enabled</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Compliance">
                    <Tag color="success" icon={<CheckCircleOutlined />}>
                      21 CFR Part 11
                    </Tag>
                  </Descriptions.Item>
                </Descriptions>

                <Button
                  type="primary"
                  icon={<BranchesOutlined />}
                  onClick={() => setImpactDrawerOpen(true)}
                >
                  View Affected Recipes ({mockStandardBlocks.find((b) => b.id === selectedBlock)?.affectedRecipes})
                </Button>
              </>
            )}
          </Space>
        </Card>

        {/* Workflow Timeline */}
        {selectedBlock ? (
          <Card
            title={<span style={{ color: textColor }}>Change Control Workflow</span>}
            style={{ marginBottom: 24, backgroundColor: cardBgColor, borderColor }}
          >
            <WorkflowTimelineAntd
              workflow={STANDARD_BLOCK_VERSIONING_WORKFLOW}
              currentStage={currentStage}
              workflowData={workflowData}
              onStageAction={handleStageAction}
              onApprovalClick={handleApprovalClick}
              isDark={isDark}
            />
          </Card>
        ) : (
          <Card style={{ backgroundColor: cardBgColor, borderColor }}>
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <BlockOutlined style={{ fontSize: 64, color: secondaryTextColor, marginBottom: 16 }} />
              <Title level={4} style={{ color: secondaryTextColor }}>
                No Standard Block Selected
              </Title>
              <Text style={{ color: secondaryTextColor }}>
                Please select a standard block from the dropdown above to view and manage its change control workflow.
              </Text>
            </div>
          </Card>
        )}

        {/* E-Signature Modal */}
        <ESignatureModalAntd
          open={signatureModalOpen}
          onClose={() => {
            setSignatureModalOpen(false);
            setSelectedStageForApproval(null);
          }}
          onSubmit={handleSignatureSubmit}
          stage={selectedStageForApproval}
          documentInfo={{
            blockId: selectedBlock,
            blockName: mockStandardBlocks.find((b) => b.id === selectedBlock)?.name,
            version: mockStandardBlocks.find((b) => b.id === selectedBlock)?.version,
            affectedRecipes: mockStandardBlocks.find((b) => b.id === selectedBlock)?.affectedRecipes
          }}
          requireMultipleSignatures={true}
          requiredSigners={
            selectedStageForApproval
              ? APPROVAL_MATRIX.standard_block_versioning[`stage${selectedStageForApproval.stage}_${selectedStageForApproval.stageCode.toLowerCase()}`] || []
              : []
          }
        />

        {/* Impact Analysis Drawer */}
        <Drawer
          title={
            <Space>
              <BranchesOutlined style={{ color: '#1890ff' }} />
              <Text strong style={{ color: textColor }}>Cross-Impact Analysis - Affected {mbmrAlias}s</Text>
            </Space>
          }
          placement="right"
          width={800}
          onClose={() => setImpactDrawerOpen(false)}
          open={impactDrawerOpen}
          styles={{
            header: { backgroundColor: cardBgColor, borderColor },
            body: { backgroundColor: cardBgColor },
          }}
        >
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Card size="small" style={{ backgroundColor: isDark ? defaultColors.palette.gray[700] : '#e6f7ff', borderColor: '#1890ff' }}>
              <Text style={{ color: textColor }}>
                Modifying this standard block will automatically trigger versioning for all {mockAffectedRecipes.length} affected {mbmrAlias}s.
                High and Medium impact {mbmrAlias}s require re-approval.
              </Text>
            </Card>

            <DataTable
              columns={impactColumns}
              data={mockAffectedRecipes}
              pagination={{ pageSize: 10 }}
              height={null}
            />
          </Space>
        </Drawer>
      </div>
    </MainCard>
  );
};

export default StandardBlockVersioning;
